<template>
  <div
    class="appointment-diary"
    :class="$attrs.class"
    :data-date="diary.summary.startDateTime.split(' ')[0]"
  >
    <h3 v-if="heading" ref="headingEl" class="diary-title">{{ heading }}</h3>

    <!-- Info -->
    <div class="diary-info">
      <div class="diary-info-icon">
        <q-icon name="fa-light fa-clock" title="Diary Time Range" />
      </div>
      <div class="diary-info-value">
        {{ durationText }}
        {{ formattedLocation && !hideLocation ? ` - ${formattedLocation}` : null }}
      </div>

      <template
        v-if="!diary.summary.supportsTimedAppointments && diary.summary.defaultDeliveryMode"
      >
        <div class="diary-info-icon">
          <q-icon v-if="defaultDeliveryModeIcon" :name="defaultDeliveryModeIcon" />
        </div>
        <div class="diary-info-value">{{ diary.summary.defaultDeliveryMode?.label }}</div>
      </template>

      <div class="diary-info-icon">
        <q-icon name="fa-solid fa-calendar-check" aria-hidden="true" title="Booked" />
      </div>
      <div class="diary-info-value">
        <span v-if="diary.summary.booked.count"
          >Booked: {{ diary.summary.booked.count }} patients ({{ diary.summary.booked.duration }})
        </span>

        <span v-else>No patients booked</span>
      </div>

      <div class="diary-info-icon">
        <q-icon name="fa-light fa-hourglass-half" aria-hidden="true" />
      </div>
      <div class="diary-info-value">
        <span v-if="diary.summary.available.duration"
          >Unallocated time: {{ diary.summary.available.duration }}
        </span>
        <span v-else class="q-ml-xs">No unallocated time</span>
      </div>
    </div>

    <!-- Actions -->
    <m-dropdown-button
      v-if="dropdownOptions.length && diary.summary.status.isActive"
      class="diary-actions"
      icon="fa-solid fa-ellipsis-vertical"
      ghost
      color="secondary"
      icon-only
      label="diary options"
      menu
      :options="dropdownOptions"
    />

    <!-- Notifications -->
    <section class="diary-notifications">
      <m-banner
        v-if="diary.summary.isAtCapacity && diary.summary.canBookUntimedAppointments"
        type="warning"
      >
        <span>All available time booked.</span>
      </m-banner>

      <m-banner
        v-if="
          diary.summary.supportsTimedAppointments &&
          diary.summary.supportsUntimedAppointments &&
          diary.summary.hasHighPriorityUntimedAppointments
        "
        type="warning"
      >
        <span>High priority untimed appointments.</span>
      </m-banner>
    </section>

    <section class="appointments">
      <!-- Timed Appointments -->
      <div v-for="(entry, i) in formattedEntries" :key="`slot-${i}`" class="appointments-group">
        <div class="regular-diary-entries-container">
          <div class="diary-entries">
            <component
              :is="displayOnly ? 'div' : 'button'"
              v-if="entry.diaryEntryType.isSlot || entry.diaryEntryType.isSqueezeInSlot"
              :class="`slot available-slot ${!displayOnly ? 'interactive medicus-outline' : ''}`"
              :style="{ '--slot-color': entry.appointmentType.colour?.value ?? 'transparent' }"
              :data-slot-color="entry.appointmentType.colour?.value ?? 'transparent'"
              @click="displayOnly ? undefined : handleSlotClicked(entry)"
              @click.right="displayOnly ? undefined : handleAvailableSlotRightClick($event, entry)"
            >
              <div class="sr-only">
                Book {{ heading }} Appointment, {{ entry.start }} for
                {{ entry.formattedDuration }}
              </div>

              <div class="slot-info__container">
                <div class="slot-info__details no-hover" aria-hidden="true">
                  <p class="start-time">{{ entry.start }}</p>

                  <p class="duration">
                    {{ entry.formattedDuration }} •
                    {{ entry.appointmentType.name }}
                  </p>
                </div>
              </div>

              <div class="overlay" aria-hidden="true">
                <span v-if="entry.diaryEntryType.isSlot">{{ slotLabel }} {{ entry.start }}</span>
                <span v-else-if="entry.diaryEntryType.isSqueezeInSlot">Squeeze in here</span>
              </div>
            </component>

            <template v-if="entry.diaryEntryType.isUnavailabilityPeriod">
              <m-staff-break-card
                v-if="entry.eventEntityType.isStaffBreakAssignment"
                :staff-break="entry"
                :allow-right-click="allowAppointmentContextMenu"
                @click="() => handleInteractiveDiaryEntryClick(entry)"
              >
                <template #context-menu-options>
                  <m-action-list ref="list" :items="getEntryActions(entry)" />
                </template>
              </m-staff-break-card>

              <m-rota-activity-assignment-card
                v-else-if="entry.eventEntityType.isRotaActivityAssignment"
                :rota-activity-assignment="entry"
                :allow-right-click="allowAppointmentContextMenu"
                @click="() => handleInteractiveDiaryEntryClick(entry)"
              >
                <template #context-menu-options>
                  <m-action-list ref="list" :items="getEntryActions(entry)" />
                </template>
              </m-rota-activity-assignment-card>

              <m-meeting-attendee-card
                v-else-if="entry.eventEntityType.isMeetingAttendee"
                :meeting-attendee="entry"
                @click="() => handleInteractiveDiaryEntryClick(entry)"
              />

              <m-absence-card
                v-else-if="entry.eventEntityType.isAbsence"
                :absence="entry"
                @click="() => handleInteractiveDiaryEntryClick(entry)"
              />
            </template>

            <m-slot-reservation-card
              v-else-if="entry.diaryEntryType.isSlotReservation"
              :slot-reservation="entry"
              :allow-right-click="allowAppointmentContextMenu"
            >
              <template #context-menu-options>
                <m-action-list ref="list" :items="getEntryActions(entry)" />
              </template>
            </m-slot-reservation-card>

            <m-staff-break-card
              v-else-if="entry.diaryEntryType.isStaffBreakAssignment"
              :staff-break="entry"
              :allow-right-click="allowAppointmentContextMenu"
              @click="() => handleInteractiveDiaryEntryClick(entry)"
            >
              <template #context-menu-options>
                <m-action-list ref="list" :items="getEntryActions(entry)" />
              </template>
            </m-staff-break-card>

            <m-rota-activity-assignment-card
              v-else-if="entry.diaryEntryType.isRotaActivityAssignment"
              :rota-activity-assignment="entry"
              :allow-right-click="allowAppointmentContextMenu"
              @click="() => handleInteractiveDiaryEntryClick(entry)"
            >
              <template #context-menu-options>
                <m-action-list ref="list" :items="getEntryActions(entry)" />
              </template>
            </m-rota-activity-assignment-card>

            <m-appointment-card
              v-else-if="entry.diaryEntryType.isAppointment"
              class="slot booked-item"
              :appointment="entry"
              :hide-reason-for-appointment="hideReasonsForAppointments"
              @click="$emit('appointment-clicked', entry)"
            />
          </div>
        </div>
      </div>

      <!-- Untimed Appointments -->
      <template v-if="showUntimedAppointmentsSection">
        <div
          v-if="diary.summary.supportsUntimedAppointments"
          class="appointments-group untimed-appointments-group"
        >
          <div
            v-if="diary.summary.supportsTimedAppointments || diary.entries.length"
            class="appointments-group-title"
            :style="{ top: headingHeight + 'px' }"
          >
            <p>Untimed Appointments</p>

            <div class="diary-info">
              <div class="diary-info-icon">
                <q-icon v-if="defaultDeliveryModeIcon" :name="defaultDeliveryModeIcon" />
              </div>
              <div class="diary-info-value">{{ diary.summary.defaultDeliveryMode?.label }}</div>
            </div>
          </div>

          <p
            v-if="!displayOnly && !diary.untimedAppointments.length"
            class="empty-text text-center q-mt-sm"
          >
            There are no appointments
          </p>

          <div class="diary-entries">
            <div
              v-for="appointment in diary.untimedAppointments"
              :key="appointment.id"
              class="appointment q-mb-md"
            >
              <m-appointment-card
                class="booked-item"
                :appointment="appointment"
                @click="$emit('appointment-clicked', appointment)"
              />
            </div>
          </div>

          <div
            v-if="!displayOnly && diary.summary.canBookUntimedAppointments"
            class="book-untimed-appointment"
            :class="{ flex: diary.untimedAppointments.length }"
          >
            <m-button
              class="book-untimed-appointment-button"
              :disabled="displayOnly"
              ghost
              label="Book untimed appointment"
              @click="$emit('book-untimed-appointment', diary)"
            />
          </div>
        </div>
      </template>
    </section>

    <MMenu v-model="isContextMenuShowing" :target="contextMenuTarget" no-auto-open>
      <m-action-list :items="slotActions" />
    </MMenu>
  </div>
</template>

<script setup lang="ts">
import { QIcon, date } from "quasar";
import MBanner from "../../MBanner/MBanner.vue";
import MButton from "../../MButton/MButton.vue";
import MActionList, { ActionListItems } from "../../MActionList/MActionList.vue";
import MMenu from "../../MMenu";
import MDropdownButton from "../../MDropdownButton";
import { AppointmentDiary, FormattedDiaryEntry, RawDiaryEntry } from "./types";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { AppointmentDeliveryMode } from "../MAppointmentDiaryEntries";
import { showDialog } from "../../../../utils/dialog";
import {
  MAppointmentCard,
  MStaffBreakCard,
  MRotaActivityAssignmentCard,
  MSlotReservationCard,
  MMeetingAttendeeCard,
  MAbsenceCard,
} from "../MAppointmentDiaryEntries";

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    diary: AppointmentDiary;
    title?: string;
    heading?: string;
    hideHeader?: boolean;
    hideLocation?: boolean;
    displayOnly?: boolean;
    showDiaryControls?: boolean;
    userHasPermissionToEdit?: boolean;
    allowAppointmentContextMenu?: boolean;
    slotLabel?: string;
    hideReasonsForAppointments?: boolean;
    hideEmptyUntimedAppointmentsSection?: boolean;
  }>(),
  {
    slotLabel: "Book",
    hideEmptyUntimedAppointmentsSection: false,
  },
);

const emit = defineEmits([
  "cancel-diary",
  "edit-diary",
  "slot-clicked",
  "appointment-clicked",
  "squeeze-patient-in",
  "book-untimed-appointment",
  "edit-slot-appointment-type",
  "convert-slot-to-break",
  "convert-slot-to-rota-activity-assignment",
  "remove-break",
  "remove-rota-activity-assignment",
  "print-diary",
  "rota-activity-assignment-clicked",
  "staff-break-assignment-clicked",
  "remove-slot-reservation",
]);

const observer = ref<ResizeObserver | null>(null);
const headingHeight = ref<number | null>(null);
const isContextMenuShowing = ref<boolean>(false);

// Template refs
const headingEl = ref<HTMLElement>();
const contextMenuTarget = ref<InstanceType<typeof HTMLElement> | null>(null);
const contextMenuSlot = ref<RawDiaryEntry | null>(null);

const slotActions = computed<ActionListItems>(() => {
  return [
    {
      label: "Change appointment type",
      onClick: editSlotAppointmentType,
    },
    {
      label: "Convert slot to break",
      onClick: convertSlotToBreak,
    },
    {
      label: "Convert slot to other activity",
      onClick: convertSlotToRotaActivityAssignment,
    },
  ];
});

function getEntryActions(entry): ActionListItems {
  const items: ActionListItems = [];

  if (entry.diaryEntryType.isUnavailabilityPeriod) {
    if (entry.eventEntityType.isStaffBreakAssignment) {
      items.push({
        label: "Remove break",
        onClick: () => removeBreak(entry.eventId),
      });
    }

    if (entry.eventEntityType.isRotaActivityAssignment) {
      items.push({
        label: `Remove activity assignment`,
        onClick: () => removeRotaActivityAssignment(entry.eventId),
      });
    }
  }

  if (entry.diaryEntryType.isStaffBreakAssignment) {
    items.push({
      label: "Remove break",
      onClick: () => removeBreak(entry.id),
    });
  }

  if (entry.diaryEntryType.isRotaActivityAssignment) {
    items.push({
      label: `Remove ${entry.title.toLowerCase()}`,
      onClick: () => removeRotaActivityAssignment(entry.id),
    });
  }

  if (entry.diaryEntryType?.isSlotReservation) {
    items.push({
      label: "Remove slot reservation",
      onClick: () => removeSlotReservation(entry.id),
    });
  }

  return items;
}

const dropdownOptions = computed(() => {
  let options = [];

  if (props.showDiaryControls) {
    if (props.userHasPermissionToEdit) {
      options.push({
        label: "Edit diary",
        click: () => emit("edit-diary", props.diary),
      });

      if (props.diary.summary.canBeCancelled) {
        options.push({
          label: "Cancel diary",
          click: cancelAppointmentDiary,
          disabled: !props.diary.summary.canBeCancelled,
        });
      }
    }

    options.push({
      label: "Print diary",
      click: () => emit("print-diary", props.diary),
    });
  }

  if (props.allowAppointmentContextMenu) {
    options.unshift({
      label: "Squeeze patient in",
      click: () => emit("squeeze-patient-in", props.diary),
    });
  }

  return options;
});

const defaultDeliveryModeIcon = computed((): string | undefined => {
  return deduceDeliveryModeIcon(props.diary?.summary?.defaultDeliveryMode);
});

const formattedLocation = computed((): string | null => {
  let location: string | undefined = props.diary.summary.site?.name;
  if (!location && !props.diary.summary.room?.name) {
    return "None recorded";
  } else if (location && !props.diary.summary.room?.name) {
    return location;
  } else if (!location && props.diary.summary.room?.name) {
    return props.diary.summary.room?.name;
  }
  return `${location}, ${props.diary.summary.room?.name}`;
});

const formattedEntries = computed((): any[] => {
  return props.diary.entries.map((entry) => formatDiaryEntry(entry as RawDiaryEntry));
});

const start = computed(() => {
  return parseDate(props.diary.summary.startDateTime);
});

const end = computed(() => {
  return parseDate(props.diary.summary.endDateTime);
});

const durationText = computed(() => {
  return date.formatDate(start.value, "HH:mm") + " - " + date.formatDate(end.value, "HH:mm");
});

const showUntimedAppointmentsSection = computed((): boolean => {
  if (props.diary.untimedAppointments.length) {
    return true;
  }

  if (props.hideEmptyUntimedAppointmentsSection) {
    return false;
  }

  return props.diary.summary.canBookUntimedAppointments;
});

onMounted(() => {
  observer.value = new ResizeObserver(handleResize);

  if (headingEl.value) {
    observer.value.observe(headingEl.value);
  }
});

onBeforeUnmount(() => {
  observer.value?.disconnect();
});

function parseDate(e: string | Date | number): Date {
  return e instanceof Date
    ? e
    : typeof e === "number"
    ? new Date(e)
    : new Date(Date.parse(e as any));
}

function formatDiaryEntry(slot: RawDiaryEntry): FormattedDiaryEntry {
  const start = parseDate(slot.startDateTime!);
  const end = parseDate(slot.endDateTime!);
  const duration = date.getDateDiff(end, start, "minutes");
  return {
    ...slot,
    id: slot.id ?? slot.appointmentId ?? slot.diaryId ?? "no-id",
    start: date.formatDate(start, "HH:mm"),
    end: date.formatDate(end, "HH:mm"),
    formattedDuration: duration === 1 ? `${duration} min` : `${duration} mins`,
    siteName: props.diary.summary.site?.name,
  };
}

function cancelAppointmentDiary() {
  let text =
    "Are you sure you would like to cancel this appointment diary? This action cannot be undone.";
  const bookedCount = props.diary?.summary.booked?.count;
  if (bookedCount) {
    text = `This appointment diary has ${bookedCount} appointment${
      bookedCount == 1 ? "" : "s"
    } that will also be cancelled.`;
    text = text + " Are you sure you would like to cancel the diary? This action cannot be undone.";
  }
  showDialog({
    title: "Cancel Diary?",
    text,
    cancelLabel: "No, don't cancel",
    okLabel: "Yes, cancel",
  }).onOk(() => {
    emit("cancel-diary", props.diary);
  });
}

function deduceDeliveryModeIcon(value?: AppointmentDeliveryMode) {
  if (!value) return "";

  if (value.isFaceToFace) {
    return "fa-solid fa-user-doctor";
  }
  if (value.isHomeVisit) {
    return "fa-solid fa-house";
  }
  if (value.isVideo) {
    return "fa-solid fa-video";
  }
  return "fa-solid fa-phone";
}

function handleSlotClicked(slot) {
  slot.diaryIsAtCapacity = props.diary.summary.isAtCapacity;

  slot.assignedPractitioners = props.diary.summary.assignees.map((a) => {
    return {
      staffId: a.id,
      name: a.displayName,
    };
  });

  emit("slot-clicked", slot);
}

function handleInteractiveDiaryEntryClick(entry) {
  if (entry.diaryEntryType.isUnavailabilityPeriod) {
    if (entry.eventEntityType.isStaffBreakAssignment) {
      emit("staff-break-assignment-clicked", entry.eventId);
    } else if (entry.eventEntityType.isRotaActivityAssignment) {
      emit("rota-activity-assignment-clicked", entry.eventId);
    }
  }

  if (entry.diaryEntryType.isRotaActivityAssignment) {
    emit("rota-activity-assignment-clicked", entry.id);
  }

  if (entry.diaryEntryType.isStaffBreakAssignment) {
    emit("staff-break-assignment-clicked", entry.id);
  }
}

function handleResize(entries: ResizeObserverEntry[]) {
  for (const entry of entries) {
    headingHeight.value = (entry.target as HTMLElement).offsetHeight - 1;
  }
}

function handleAvailableSlotRightClick(event: MouseEvent, slot: RawDiaryEntry) {
  if (!props.allowAppointmentContextMenu) return;

  // Only show context menu if no modifier key is being held when right clicking
  if (!(event.ctrlKey || event.shiftKey || event.altKey || event.metaKey)) {
    event.preventDefault();
    let currentEl: HTMLElement | null = event.target as HTMLElement;
    let slotEl = null;

    // Target may be an element inside of the slot button
    // Keep checking parent elements until a button found
    while (slotEl === null) {
      if (currentEl.tagName.toLowerCase() === "button" && currentEl.classList.contains("slot")) {
        slotEl = currentEl;
        break;
      }

      currentEl = currentEl.parentElement;

      if (currentEl === null) {
        break;
      }
    }

    if (slotEl) {
      contextMenuTarget.value = slotEl; // Don't use event target
      contextMenuSlot.value = slot;
      isContextMenuShowing.value = !isContextMenuShowing.value; // Toggle show on right click
    }
  }
}

function editSlotAppointmentType() {
  if (contextMenuSlot.value) {
    emit("edit-slot-appointment-type", contextMenuSlot.value);
  }
}

function convertSlotToBreak() {
  if (contextMenuSlot.value) {
    emit("convert-slot-to-break", contextMenuSlot.value);
  }
}

function convertSlotToRotaActivityAssignment() {
  if (contextMenuSlot.value) {
    emit("convert-slot-to-rota-activity-assignment", contextMenuSlot.value);
  }
}

function removeBreak(breakId: string) {
  emit("remove-break", breakId);
}

function removeRotaActivityAssignment(rotaActivityAssignmentId: string) {
  emit("remove-rota-activity-assignment", rotaActivityAssignmentId);
}

function removeSlotReservation(slotReservationId: string) {
  emit("remove-slot-reservation", slotReservationId);
}
</script>

<style lang="scss">
:root {
  --appointment-diary-bg: var(--grey-lightest);
  --appointment-diary-border-radius: 6px;
  --appointment-diary-padding: var(--gap-3);
  --slot-border-radius: 4px;
  --transition: all 140ms ease-out;
}

.appointment-diary {
  display: flex;
  flex-direction: column;
  background: var(--appointment-diary-bg);
  border-radius: var(--appointment-diary-border-radius);
  position: relative;
  padding-bottom: var(--appointment-diary-padding);
  height: 100%;
  @media (min-width: 768px) {
    height: auto;
  }
}

.diary-title {
  position: sticky;
  top: 0;
  background: var(--appointment-diary-bg);
  z-index: 100;
  font-size: 14px;
  padding-top: var(--appointment-diary-padding);
  padding-left: var(--appointment-diary-padding);
  padding-right: 40px;
  display: flex;
  align-items: flex-start;
  color: var(--text-color-light);
  border-top-left-radius: var(--appointment-diary-border-radius);
  border-top-right-radius: var(--appointment-diary-border-radius);
}

.diary-info {
  display: grid;
  grid-template-columns: max-content auto;
  gap: 0 6px;
  padding: 0 var(--appointment-diary-padding);
  margin-bottom: var(--gap-2);
  font-size: 14px;
  color: var(--grey-darkest);
  font-weight: 400;

  .diary-info-icon {
    grid-column-start: 1;
    display: flex;
    place-items: center;
  }

  .diary-info-value {
    grid-column-start: 2;
  }
}

.diary-actions {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 600;
  aspect-ratio: 1 / 1;
  width: auto;
}

.appointments-group {
  + .appointments-group {
    margin-top: var(--gap-2);
  }
}

.appointments-group-title {
  position: sticky;
  background: linear-gradient(to bottom, var(--appointment-diary-bg) 66%, transparent);
  z-index: 50;
  font-size: 16px;
  font-weight: 600;
  padding: 0 var(--appointment-diary-padding) var(--gap-1);
  // padding-bottom: 15px;
  line-height: 1.4;

  .diary-info {
    padding-left: 0;
    margin-bottom: 5px;
  }
}

.appointments {
  .diary-entries {
    padding: 0 var(--appointment-diary-padding);
  }
  .slot {
    position: relative;
    transition: var(--transition);

    .slot-info__container {
      display: flex;
      align-items: center;

      .slot-info__details {
        line-height: 1.2;

        .start-time {
          font-weight: 600;
          margin-bottom: 4px;
          transition: var(--transition);
        }

        .duration {
          color: var(--text-color-light);
          transition: all 140ms ease-out;
        }
      }
    }

    + .slot {
      margin-top: var(--gap-2);
    }
  }

  .available-slot {
    background: var(--white-overylay);
    width: 100%;
    padding: var(--gap-1) var(--gap-3);
    border: 1.5px dashed var(--grey-darker);
    border-radius: var(--slot-border-radius);

    position: relative;

    &::after {
      content: "";
      position: absolute;
      left: -1.5px;
      top: -1.5px;
      bottom: -1.5px;
      width: 6px;
      background-color: var(--slot-color, transparent);
      border-top-left-radius: var(--slot-border-radius);
      border-bottom-left-radius: var(--slot-border-radius);
    }
  }

  .booked-item {
    width: 100%;
  }

  .overlay {
    display: block;
    text-decoration: underline;

    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;

    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--theme-blue);
    color: #ffffff;
    font-weight: 600;
    border-radius: var(--slot-border-radius);
    transition: all 140ms ease-out;
  }

  .interactive {
    &:hover {
      border-style: solid;
      border-color: var(--theme-blue);
      cursor: pointer;

      box-shadow: var(--box-shadow);

      .start-time,
      .duration {
        opacity: 0;
      }

      .overlay {
        opacity: 1;
      }
    }
  }

  .unavailability-period {
    height: 100%;
    min-height: 60px;
    width: 100%;
    font-family: Arial, sans-serif;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 150%;
  }
}

.diary-notifications {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 var(--appointment-diary-padding);

  > :last-child {
    margin-bottom: 20px;
  }
}

.untimed-appointments-group {
  margin-top: 30px;

  .book-untimed-appointment {
    margin-top: 14px;
    padding: 0 var(--appointment-diary-padding);

    .book-untimed-appointment-button {
      width: fit-content;
      margin-top: 5px;
    }
  }

  .empty-text {
    border-radius: 6px;
    margin: 0 14px;
  }
}
</style>
